/**
 * HeroStatsBand — the proof strip that sits under the hero.
 *
 * Each column is a value over a caption. Two carry a rolling numeral, the
 * third carries the Tebex Legends award art in the same slot.
 *
 * Numbers roll into place like a slot machine the first time the band scrolls
 * into view: each digit is a column of 0–9 that slides up to its final value,
 * with a per-digit delay so they land left-to-right rather than all at once.
 */

import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import '@/styles/components/HeroStatsBand.css';

/* A caption's job is to say what the figure means, not to name it again.
 *
 * All three used to do the naming. "Customers" under a customer count adds
 * nothing. "Star Review Rating" under a 5.0 with a star beside it says the
 * same thing three times in one column. "Silver Tier · Tebex Legends" under
 * artwork that already reads SILVER TIER repeats the only word the artwork
 * could manage on its own.
 *
 * These say what each figure is worth to someone deciding. Nothing here
 * claims anything the band did not already claim — no new numbers, no
 * timeframe, no invented record.
 *
 * The third entry is the Tebex Legends award. It is a tier in that
 * programme, not a partnership, and the revenue behind the tier stays
 * private — so the caption names the programme and stops. */
const STATS = [
  { value: '3,000+', caption: 'Customers before you' },
  { value: '5.0', caption: 'The top of the scale', star: true },
  {
    /* The award art fills the value row where the other two carry a numeral.
       The caption still has to carry the programme name in words: the artwork
       says SILVER TIER and nothing about where the tier comes from. */
    value: 'Silver Tier',
    caption: 'Tebex Legends, earned',
    badge: '/img/tebex-legends-silver.webp',
  },
];

/* Three copies of 0–9 so the reel spins two whole turns before landing on the
   real digit — one pass alone just slides, it does not read as a slot machine.
   Each digit box is exactly 1em tall, so the landing offset is (20 + n)em. */
const TURNS = 2;
const STRIP = Array.from({ length: 10 * (TURNS + 1) }, (_, i) => i % 10);

/* One rolling column. Non-digits (+ , .) are static so they don't spin. */
const Reel = ({ char, delay, rolling }) => {
  if (!/\d/.test(char)) {
    return <span className="stat-static">{char}</span>;
  }
  const landing = TURNS * 10 + Number(char);
  return (
    <span className="stat-reel" aria-hidden="true">
      <span
        className="stat-reel__strip"
        style={{
          transform: rolling ? `translateY(-${landing}em)` : 'translateY(0)',
          transitionDelay: `${delay}ms`,
        }}
      >
        {STRIP.map((d, i) => (
          <span key={i} className="stat-reel__digit">{d}</span>
        ))}
      </span>
    </span>
  );
};

const HeroStatsBand = () => {
  const ref = useRef(null);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setRolling(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setRolling(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  /* Two numbers written straight onto the element for the stylesheet to use.
   *
   *   --enter  0 while the panel is still below the fold, 1 once it has
   *            risen into the reading area. Drives the tilt, so the slab
   *            settles flat as it arrives instead of being flat all along.
   *   --pass   0 to 1 across the panel's whole journey through the viewport.
   *            Drives the highlight travelling across the glass, which is
   *            what makes it read as a surface catching a light rather than
   *            a painted rectangle.
   *
   * Written as custom properties rather than as state: this runs on every
   * scroll frame, and re-rendering React sixty times a second to move a
   * highlight would be absurd. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      el.style.setProperty('--enter', '1');
      el.style.setProperty('--pass', '0.5');
      return undefined;
    }

    let frame = 0;
    const measure = () => {
      frame = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const clamp = (n) => Math.min(1, Math.max(0, n));
      /* enter: from the moment the top edge appears to the moment the panel
         has climbed a third of the screen. */
      el.style.setProperty('--enter', clamp((vh - r.top) / (vh * 0.55)).toFixed(4));
      /* pass: the centre of the band relative to the centre of the screen. */
      el.style.setProperty('--pass', clamp((vh - r.top) / (vh + r.height)).toFixed(4));
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section className="hero-stats" ref={ref} aria-label="Store highlights">
      {/* The light the glass has to bend. Without something behind it a
          backdrop filter over a flat black page does nothing at all. */}
      <div className="hero-stats__lamp" aria-hidden="true" />

      {/* Tilt on the outside, glass on the inside — a backdrop filter and a 3D
          transform on the same element fight each other. */}
      <div className="hero-stats__tilt">
        <div className="hero-stats__panel">
          <span className="hero-stats__rim" aria-hidden="true" />
          <span className="hero-stats__spec" aria-hidden="true" />

      {/* A heading, not a caption. It was a full sentence set at sixteen
          pixels doing a headline's work at a footnote's size, which is why it
          disappeared above a row of sixty-four pixel numerals. Three words
          can hold that position; a sentence cannot. */}
      <h2 className="hero-stats__lead">Proven in production.</h2>

      <ul className="hero-stats__grid">
        {STATS.map((s) => (
          <li className="hero-stat" key={s.caption}>
            <span className="hero-stat__value">
              {/* The real value stays readable to assistive tech and to search
                  engines; the reels, star and badge art are decorative. */}
              <span className="sr-only">{s.value}</span>
              {s.badge ? (
                <img
                  className={`stat-badge${rolling ? ' is-in' : ''}`}
                  src={s.badge}
                  alt=""
                  aria-hidden="true"
                  width="455"
                  height="233"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                [...s.value].map((c, i) => (
                  <Reel key={`${s.caption}-${i}`} char={c} delay={i * 110} rolling={rolling} />
                ))
              )}
              {s.star && (
                <Star className={`stat-star${rolling ? ' is-in' : ''}`} aria-hidden="true" />
              )}
            </span>

            <span className="hero-stat__caption">{s.caption}</span>
          </li>
        ))}
      </ul>
        </div>
      </div>
    </section>
  );
};

export default HeroStatsBand;
